import { Controller } from "@hotwired/stimulus"

// One group of Event settings (Restaurant::PartySettingsController). Save posts the group's form in the background and
// swaps in the group the server sends back: still open, marked Saved, or holding the reason a save was refused. The page
// never reloads, so it stays where it was and the other groups keep anything typed but not saved. The setup checklist
// above the groups comes from the same page, so it follows the save. Anything else the server answers (signed out, a
// server error) or a dropped connection falls back to a plain submit.
export default class extends Controller {
  async save(event) {
    const form = event.target
    if (!form.matches("form.ps-group__form")) return

    event.preventDefault()
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content || ""
      const response = await fetch(form.action, {
        method: "POST",
        body: new FormData(form),
        credentials: "same-origin",
        headers: { "X-Requested-With": "XMLHttpRequest", "X-CSRF-Token": token, Accept: "text/html" }
      })
      const page = response.ok || response.status === 422 ? new DOMParser().parseFromString(await response.text(), "text/html") : null
      const group = page?.getElementById(this.element.id)
      if (!group) return form.submit()

      // A ticked setup step shortens the checklist above; scroll by the difference so the group stays where it was in
      // a browser without scroll anchoring too. Bootstrap's smooth scrolling would glide it back after a visible jump,
      // so this one scroll is instant. It's set inline rather than as behavior: "instant": a browser that doesn't know a
      // behavior value throws, and the catch below would post the form a second time.
      const top = this.element.getBoundingClientRect().top
      const fresh = document.importNode(group, true)
      this.swap(page, "ps-setup")
      this.element.replaceWith(fresh)
      const root = document.documentElement
      root.style.scrollBehavior = "auto"
      window.scrollBy(0, fresh.getBoundingClientRect().top - top)
      root.style.scrollBehavior = ""
      fresh.querySelector('.ps-group__form [type="submit"]')?.focus({ preventScroll: true })
    } catch {
      form.submit()
    }
  }

  // A change shows Unsaved changes beside Save, and typing again after a save takes the Saved mark away.
  edited() {
    this.element.classList.add("ps-group--edited")
    this.element.querySelector(".ps-group__saved")?.remove()
  }

  swap(page, id) {
    const fresh = page.getElementById(id)
    if (fresh) document.getElementById(id)?.replaceWith(document.importNode(fresh, true))
  }
}
